// ./src/components/features/Dashboard/DashboardRecentActivity.tsx
import React from "react";
import { Paper, Title, Text, Group, Stack, Box } from "@mantine/core";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { AppButton, EmptyState } from "@/components/generic";
import { SubmissionStatusBadge } from "@/components/features";
import { DashboardActivityItem } from "@/utils/api";
import { SUBMISSION_STATUSES } from "@/config";

dayjs.extend(relativeTime);

export interface DashboardRecentActivityProps {
  recentActivity: DashboardActivityItem[];
  onViewSubmission: (item: DashboardActivityItem) => void;
  onNavigateToSubmissions: () => void;
}

export const DashboardRecentActivity: React.FC<
  DashboardRecentActivityProps
> = ({ recentActivity, onViewSubmission, onNavigateToSubmissions }) => {
  return (
    <Paper p="md" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={4} size="h5">
          Recent Activity
        </Title>
        <AppButton
          variant="secondary"
          size="xs"
          onClick={onNavigateToSubmissions}
        >
          View All
        </AppButton>
      </Group>

      {recentActivity.length === 0 ? (
        <EmptyState
          display
          title="No submissions yet"
          description="Submissions will appear here once your forms start receiving data"
        />
      ) : (
        <Stack gap="xs">
          {recentActivity.map((item) => (
            <ActivityItem
              key={item.id}
              item={item}
              onClick={onViewSubmission}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

// Activity item component
interface ActivityItemProps {
  item: DashboardActivityItem;
  onClick: (item: DashboardActivityItem) => void;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ item, onClick }) => {
  const isNew = item.status === SUBMISSION_STATUSES.RECEIVED;

  return (
    <Paper
      p="sm"
      radius="sm"
      withBorder
      style={{
        cursor: "pointer",
        backgroundColor: isNew ? "var(--mantine-color-blue-0)" : undefined,
        transition: "background-color 0.2s ease",
      }}
      onClick={() => onClick(item)}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" mb={2}>
            <Text size="sm" fw={isNew ? 600 : 500} c="blue">
              {item.formName}
            </Text>
            <SubmissionStatusBadge status={item.status} />
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              {dayjs(item.submittedAt).fromNow()}
            </Text>
            {item.submitterEmail && (
              <>
                <Text size="xs" c="dimmed">
                  •
                </Text>
                <Text size="xs" c="dimmed" truncate style={{ maxWidth: 200 }}>
                  {item.submitterEmail}
                </Text>
              </>
            )}
            {item.summary && (
              <>
                <Text size="xs" c="dimmed">
                  •
                </Text>
                <Text size="xs" c="dimmed">
                  {item.summary}
                </Text>
              </>
            )}
          </Group>
        </Box>

        {/* New submission indicator */}
        {isNew && (
          <Box
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "var(--mantine-color-blue-6)",
              flexShrink: 0,
              marginTop: 6,
            }}
          />
        )}
      </Group>
    </Paper>
  );
};
